import { useEffect, useRef, useState } from 'react';

type FilterTextInputProps = {
  id: string;
  columnName: string;
  value: string;
  onChange: (columnName: string, value: string) => void;
  ariaLabel?: string;
  title?: string;
  placeholder?: string;
  /** Restrict typing to digits (TestId). Default true. */
  digitsOnly?: boolean;
  debounceMs?: number;
  /** Focus the input when it mounts (e.g. after clicking the filter icon). */
  autoFocus?: boolean;
};

export default function FilterTextInput({
  id,
  columnName,
  value,
  onChange,
  ariaLabel,
  title,
  placeholder = 'Type to filter…',
  digitsOnly = true,
  debounceMs = 300,
  autoFocus = false,
}: FilterTextInputProps) {
  const label = ariaLabel || title || `Filter ${columnName}`;
  const [draft, setDraft] = useState(value || '');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setDraft(value || '');
  }, [value]);

  useEffect(() => {
    if (!autoFocus) return;
    inputRef.current?.focus();
  }, [autoFocus]);

  useEffect(() => {
    const normalized = String(draft ?? '');
    const committed = String(value ?? '');
    if (normalized === committed) return undefined;

    const handle = window.setTimeout(() => {
      onChange(columnName, normalized);
    }, debounceMs);

    return () => window.clearTimeout(handle);
  }, [draft, value, columnName, onChange, debounceMs]);

  function commitNow(next: string) {
    setDraft(next);
    if (next !== (value || '')) {
      onChange(columnName, next);
    }
  }

  return (
    <input
      ref={inputRef}
      id={id}
      type="text"
      className="filter-select filter-text-input"
      value={draft}
      placeholder={placeholder}
      title={title || label}
      aria-label={label}
      inputMode={digitsOnly ? 'numeric' : 'text'}
      autoComplete="off"
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => {
        const raw = e.target.value;
        setDraft(digitsOnly ? raw.replace(/\D/g, '') : raw);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commitNow(draft);
        }
      }}
      onBlur={() => commitNow(draft)}
    />
  );
}
